import { timer as observableTimer, Observable, Subscription } from 'rxjs';
import { Component, OnInit, OnChanges, OnDestroy, Input, SimpleChanges, TemplateRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Doc } from '../../../models/doc';
import { DocumentContact } from '../../../models/document-contact';

import { AlertService } from '../../../services/common/alert.service';
import { AuthService } from '../../../services/common/auth.service';
import { DocumentService } from '../../../services/documents/document.service';
import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { FaviconService } from '../../../services/common/favicon.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';

declare var jQuery: any;
declare var Noty: any;
declare var tinymce: any;

@Component({
  selector: 'app-document-sidebar',
  templateUrl: './document-sidebar.component.html',
  styleUrls: ['./document-sidebar.component.css'],
})
export class DocumentSidebarComponent implements OnInit, OnDestroy {
  @Input() matter_id: any;

  autosaveInterval: any = 1;
  autosaveIntervals: Array<number> = [];
  canSendMailMerge: boolean = false;
  disableAutosave: boolean = false;
  fontSizes: Array<number> = [];
  htmlDebug: boolean = false;
  optionsExpand: any = {};
  mailTypes: Array<string> = ['basic', 'overnight', 'certified', 'certified return receipt requested', 'registered'];
  marginType: string = 'simple';
  marginSettings: any = {
    margin_selection: 'simple',
    margin: 'default',
    margin_top: 'default',
    margin_right: 'default',
    margin_bottom: 'default',
    margin_left: 'default',
  };
  marginOptions: Array<string> = ['default', '0.4"', '0.75"', '0.9"', '1.0"', '1.2"', '1.3"', ' 1.4"', ' 1.5"', '3.0"'];
  fontSize: number = 12;
  doc: Doc | null;
  useDefaultName: boolean = true;
  tagsList: string | null;
  extraHistoryMessage: string | null;
  signatureUsers: Array<any> = [];
  letterContacts: Array<any> = [];
  involvements: any = {};
  documentToContacts: Array<DocumentContact> = [];
  documentCcContacts: Array<DocumentContact> = [];
  documentBccContacts: Array<DocumentContact> = [];
  outgoingJobs: Array<any> = [];
  generatingPreview: boolean = false;

  documentToSortOptions: any = {
    ghostClass: 'ghost-class',
    chosenClass: 'chosen-class',
    dragClass: 'drag-class',
    handle: '.draggable-elem',
    onUpdate: (event: any) => {
      this.updateToPosition();
    },
  };

  documentCcSortOptions: any = {
    ghostClass: 'ghost-class',
    chosenClass: 'chosen-class',
    dragClass: 'drag-class',
    handle: '.draggable-elem',
    onUpdate: (event: any) => {
      this.updateCcPosition();
    },
  };

  autosaveTimer: Observable<number>;

  autosaveTimerSubscription: Subscription | null;

  constructor(private alertService: AlertService, private authService: AuthService, private documentService: DocumentService, private documentStoreService: DocumentStoreService, private faviconService: FaviconService, private matterStoreService: MatterStoreService, private modalService: NgbModal) {
    this.documentStoreService.documentSidebarComponent = this;
    for (let i = 1; i <= 60; i++) this.autosaveIntervals.push(i);
    for (let i = 5; i < 100; i++) this.fontSizes.push(i);
  }

  // Component lifecycle methods
  ngOnInit() {
    this.autosaveTimer = observableTimer(5000, 5000);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.matter_id && changes.matter_id.currentValue) {
      console.log('matter id change observed in document sidebar, resetting all variables');
      this.autosaveInterval = 1;
      this.canSendMailMerge = false;
      this.disableAutosave = false;
      this.htmlDebug = false;
      this.optionsExpand = {};
      this.marginType = 'simple';
      this.marginSettings = {
        margin_selection: 'simple',
        margin: 'default',
        margin_top: 'default',
        margin_right: 'default',
        margin_bottom: 'default',
        margin_left: 'default',
      };
      this.fontSize = 12;
      this.doc = null;
      this.useDefaultName = true;
      this.tagsList = null;
      this.extraHistoryMessage = null;
      this.signatureUsers = [];
      this.letterContacts = [];
      this.involvements = {};
      this.documentToContacts = [];
      this.documentCcContacts = [];
      this.documentBccContacts = [];
      this.outgoingJobs = [];
      this.clearTimerSubscription();
    }
  }

  ngOnDestroy() {
    this.documentStoreService.documentSidebarComponent = null;
  }

  // Component methods
  addDocumentContact(involvement: any, type: string) {
    let documentContact = new DocumentContact(involvement, type);
    switch (type) {
      case 'To':
        this.pushDocumentContact(documentContact, this.documentToContacts);
        this.updateToPosition();
        break;
      case 'CC':
        this.pushDocumentContact(documentContact, this.documentCcContacts);
        this.updateCcPosition();
        break;
      case 'BCC':
        this.pushDocumentContact(documentContact, this.documentBccContacts);
        break;
    }
  }

  attachmentsInclude() {
    if (!this.doc!.attachments_included) {
      for (let documentContact of this.documentCcContacts) documentContact.send_attachments = false;
      for (let documentContact of this.documentBccContacts) documentContact.send_attachments = false;
    }
  }

  autosave(timesCalled: number) {
    if (!this.disableAutosave && (timesCalled * 5) % (this.autosaveInterval * 60) == 0 && timesCalled > 0) this.saveDocument(true, false, false);
  }

  cancelDocument(noPromptBillingSave: boolean = false) {
    if (noPromptBillingSave || !this.matterStoreService.is_matter_user || !this.matterStoreService.billingRecordTimeComponent) {
      if (this.doc!.id) {
        this.alertService.alert_status_info_overlay('Cancelling Document');
        this.documentService
          .cancel(this.doc!.id)
          .then((response_json) => {
            this.alertService.alert_status_clear_overlay();
            this.newDocument(true);
            this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
          })
          .catch(() => {
            this.alertService.alert_status_clear_overlay();
            this.alertService.alert_status_danger('Error in cancelling document');
          });
      } else {
        this.newDocument();
      }
      this.clearTimerSubscription();
    } else
      this.matterStoreService.billingRecordTimeComponent.promptBillingSave('document')!.then((value) => {
        if (value == 'cancel') return;
        if (this.doc!.id) {
          this.alertService.alert_status_info_overlay('Cancelling Document');
          this.documentService
            .cancel(this.doc!.id)
            .then((response_json) => {
              this.alertService.alert_status_clear_overlay();
              this.newDocument(true);
              this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
            })
            .catch(() => {
              this.alertService.alert_status_clear_overlay();
              this.alertService.alert_status_danger('Error in cancelling document');
            });
        } else {
          this.newDocument();
        }
        this.clearTimerSubscription();
      });
  }

  clearAutosave() {}

  clearLetterOptions() {
    this.documentToContacts = [];
    this.documentCcContacts = [];
    this.documentBccContacts = [];
  }

  clearTimerSubscription() {
    console.log('Clearing document autosave subscription', this.autosaveTimerSubscription);
    if (this.autosaveTimerSubscription) {
      this.autosaveTimerSubscription.unsubscribe();
      this.autosaveTimerSubscription = null;
    }
  }

  defaultNameChange() {
    if (this.useDefaultName) this.doc!.name = '';
  }

  fetchDocumentContact(type: string, documentContact: any) {
    let involvement_id = documentContact[documentContact.selectedIndex].value;
    if (this.involvements[involvement_id]) {
      this.addDocumentContact(this.involvements[involvement_id], type);
      documentContact.selectedIndex = 0;
    } else
      this.documentService.fetchContact(involvement_id).then((response_json) => {
        this.involvements[involvement_id] = response_json;
        this.addDocumentContact(response_json, type);
        documentContact.selectedIndex = 0;
      });
  }

  marginChange(marginType: string) {
    this.marginSettings.margin_selection = marginType;
  }

  newDocument(noPromptBillingSave: boolean = false) {
    if (noPromptBillingSave || !this.matterStoreService.is_matter_user || !this.matterStoreService.billingRecordTimeComponent) {
      this.clearTimerSubscription();
      this.optionsExpand = {};
      this.marginType = 'simple';
      this.marginSettings = {
        margin_selection: 'simple',
        margin: 'default',
        margin_top: 'default',
        margin_right: 'default',
        margin_bottom: 'default',
        margin_left: 'default',
      };
      this.fontSize = 12;
      this.doc = null;
      this.useDefaultName = true;
      this.tagsList = null;
      this.extraHistoryMessage = null;
      this.documentToContacts = [];
      this.documentCcContacts = [];
      this.documentBccContacts = [];
      this.outgoingJobs = [];
      this.documentStoreService.documentFormComponent!.newDocument();
    } else
      this.matterStoreService.billingRecordTimeComponent.promptBillingSave('document')!.then((value) => {
        if (value == 'cancel') return;
        this.optionsExpand = {};
        this.marginType = 'simple';
        this.marginSettings = {
          margin_selection: 'simple',
          margin: 'default',
          margin_top: 'default',
          margin_right: 'default',
          margin_bottom: 'default',
          margin_left: 'default',
        };
        this.fontSize = 12;
        this.doc = null;
        this.useDefaultName = true;
        this.tagsList = null;
        this.extraHistoryMessage = null;
        this.documentToContacts = [];
        this.documentCcContacts = [];
        this.documentBccContacts = [];
        this.outgoingJobs = [];
        this.documentStoreService.documentFormComponent!.newDocument();
        this.clearTimerSubscription();
      });
  }

  openOutgoingModal(outgoingFormModal: TemplateRef<any>) {
    if (this.documentToContacts.length < 1) {
      this.alertService.alert_status_danger('Atleast one "To" contact must be present');
      return;
    }
    this.saveDocument(false, true).then((response_json) => {
      if (!response_json) return;
      const sidebar = document.getElementById('content-sidebar');
      if (sidebar) sidebar.style.display = 'none';
      this.modalService.open(outgoingFormModal, { size: 'xl', windowClass: 'outgoing-modal-wide' }).result.then(
        (result) => {
          if (sidebar) sidebar.style.display = '';
          if (result == 'success') {
          }
        },
        (reason) => {
          if (sidebar) sidebar.style.display = '';
        }
      );
    });
  }

  previewDocument(inline: boolean = false) {
    this.generatingPreview = true;
    let docTemplateItem: any = this.documentStoreService.documentFormComponent!.docTemplateItem;
    let documentParts: any = this.doc!.document_parts;
    let docTemplate: any = this.documentStoreService.documentFormComponent!.docTemplate;
    let inputs: any = '';
    let matter_id: any = this.doc!.matter_id;
    // Add authentication token for the form POST
    const token = window.localStorage.getItem('token');
    if (token) {
      inputs += '<input type="hidden" name="token" value="' + token + '" />';
    }
    inputs += '<input type="hidden" name="initial_run"          value="' + true + '" />';
    inputs += '<input type="hidden" name="matter_id"            value="' + matter_id + '" />';
    inputs += '<input type="hidden" name="is_letter"            value="' + this.doc!.is_letter + '" />';
    inputs += '<input type="hidden" name="is_formal"            value="' + this.doc!.is_formal + '" />';
    inputs += '<input type="hidden" name="attachments_included" value="' + this.doc!.attachments_included + '" />';
    if (docTemplateItem.model == 'DocumentTemplate' || docTemplateItem.model == 'document_template') {
      inputs += '<input type="hidden" name="document_template_id" value="' + docTemplateItem.id + '" />';
      // Also send DocTemplate info for watermark/logo if the DocumentTemplate has an associated DocTemplate
      if (docTemplate && docTemplate.logo_id) {
        inputs += '<input type="hidden" name="doc_template_id" value="' + docTemplate.id + '" />';
        inputs += '<input type="hidden" name="logo_id" value="' + docTemplate.logo_id + '" />';
        inputs += '<input type="hidden" name="watermark_start_page" value="' + docTemplate.watermark_start_page + '" />';
      }
    } else {
      inputs += '<input type="hidden" name="doc_template_id" value="' + docTemplate.id + '" />';
      inputs += '<input type="hidden" name="logo_id" value="' + docTemplate.logo_id + '" />';
      inputs += '<input type="hidden" name="watermark_start_page" value="' + docTemplate.watermark_start_page + '" />';
    }
    for (var i in documentParts) {
      let documentPart = documentParts[i];
      for (let editor of tinymce.editors) if (editor.id == `tinymce-${i}-document-part-${i}`) documentPart.contents = editor.getContent();
      inputs += '<textarea name="document_parts[][contents]">' + (documentPart.contents || '').replace(/'/g, "'") + '</textarea>';
      inputs += '<input type="hidden" name="document_parts[][include_part]" value="' + documentPart.include_part + '" />';
      inputs += '<input type="hidden" name="document_parts[][name]" value="' + documentPart.name + '" />';
      if (documentPart.type == 'signature') {
        inputs += '<input type="hidden" name="signature_matter_user_id" value="' + this.doc!.signature_matter_user_id + '" />';
        inputs += '<input type="hidden" name="signature_idx" value="' + i + '" />';
      } else if (documentPart.type == 're') inputs += '<input type="hidden" name="re_idx" value="' + i + '" />';
      else if (documentPart.type == 'cc') inputs += '<input type="hidden" name="cc_idx" value="' + i + '" />';
      else if (documentPart.type == 'close') inputs += '<input type="hidden" name="close_idx" value="' + i + '" />';
      inputs += '<input type="hidden" name="document_parts[][type]" value="' + documentPart.type + '" />';
    }
    for (let documentToContact of this.documentToContacts) {
      inputs += '<input type="hidden" name="document_to_contacts[][copy_of_contact]" value="' + documentToContact.copy_of_contact + '" />';
      inputs += '<input type="hidden" name="document_to_contacts[][copy_of_address]" value="' + (documentToContact.copy_of_address || '').replace(/\n/g, '<br>').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') + '" />';
      inputs += '<input type="hidden" name="document_to_contacts[][salutation]" value="' + this.involvements[documentToContact.involvement_id].contact.salutation + '" />';
      inputs += '<input type="hidden" name="document_to_contacts[][first_name_salutation]" value="' + (this.involvements[documentToContact.involvement_id].contact.salutation_fname || this.involvements[documentToContact.involvement_id].contact.first_name_salutation) + '" />';
      inputs += '<input type="hidden" name="document_to_contacts[][send_by_email]" value="' + documentToContact.send_by_email + '" />';
      inputs += '<input type="hidden" name="document_to_contacts[][send_by_fax]" value="' + documentToContact.send_by_fax + '" />';
      inputs += '<input type="hidden" name="document_to_contacts[][copy_of_email_address]" value="' + (documentToContact.copy_of_email_address || '').replace(/\n/g, '<br>').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') + '" />';
      inputs += '<input type="hidden" name="document_to_contacts[][copy_of_fax]" value="' + (documentToContact.copy_of_fax || '').replace(/\n/g, '<br>').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') + '" />';
    }
    for (let documentCcContact of this.documentCcContacts) {
      inputs += '<input type="hidden" name="document_cc_contacts[][copy_of_contact]" value="' + documentCcContact.copy_of_contact + '" />';
      inputs += '<input type="hidden" name="document_cc_contacts[][copy_of_address]" value="' + (documentCcContact.copy_of_address || '').replace(/\n/g, '<br>').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') + '" />';
      inputs += '<input type="hidden" name="document_cc_contacts[][salutation]" value="' + this.involvements[documentCcContact.involvement_id].contact.salutation + '" />';
      inputs += '<input type="hidden" name="document_cc_contacts[][first_name_salutation]" value="' + this.involvements[documentCcContact.involvement_id].contact.first_name_salutation + '" />';
      inputs += '<input type="hidden" name="document_cc_contacts[][send_by_mail]" value="' + documentCcContact.send_by_mail + '" />';
      inputs += '<input type="hidden" name="document_cc_contacts[][send_by_email]" value="' + documentCcContact.send_by_email + '" />';
      inputs += '<input type="hidden" name="document_cc_contacts[][send_by_fax]" value="' + documentCcContact.send_by_fax + '" />';
      inputs += '<input type="hidden" name="document_cc_contacts[][send_attachments]" value="' + documentCcContact.send_attachments + '" />';
    }
    if (this.htmlDebug == true) inputs += '<input type="hidden" name="html_debug" value="true" />';
    inputs += '<input type="hidden" name="margin_settings[margin_selection]" value="' + this.marginSettings.margin_selection + '" />';
    inputs += '<input type="hidden" name="margin_settings[margin]" value="' + this.marginSettings.margin + '" />';
    inputs += '<input type="hidden" name="margin_settings[margin_top]" value="' + this.marginSettings.margin_top + '" />';
    inputs += '<input type="hidden" name="margin_settings[margin_right]" value="' + this.marginSettings.margin_right + '" />';
    inputs += '<input type="hidden" name="margin_settings[margin_bottom]" value="' + this.marginSettings.margin_bottom + '" />';
    inputs += '<input type="hidden" name="margin_settings[margin_left]" value="' + this.marginSettings.margin_left + '" />';
    inputs += '<input type="hidden" name="font_size" value="' + (this.fontSize || 12) + '" />';
    if (inline) {
      let iframe = this.documentStoreService.documentFormComponent!.previewIframeElement.nativeElement;
      jQuery(iframe.parentElement).css('height', '30cm');
      inputs += '<input type="hidden" name="disposition" value="inline" />';
      // Listen for iframe load to clear the loading state
      jQuery(iframe).one('load', () => {
        this.generatingPreview = false;
      });
      jQuery('<form target="' + iframe.getAttribute('name') + '" action="/documents/preview.pdf" method="post">' + inputs + '</form>')
        .appendTo('body')
        .submit()
        .remove();
      this.scrollToPreview();
    } else {
      inputs += '<input type="hidden" name="disposition" value="inline" />';
      jQuery('<form target="_blank" action="/documents/preview.pdf" method="post">' + inputs + '</form>')
        .appendTo('body')
        .submit()
        .remove();
      // For download, reset after a brief delay since we can't detect download completion
      setTimeout(() => {
        this.generatingPreview = false;
      }, 3000);
    }
  }

  previewInlineHtml() {}

  pushDocumentContact(documentContact: DocumentContact, documentContacts: Array<DocumentContact>) {
    for (let dc of documentContacts) {
      if (dc.involvement_id == documentContact.involvement_id) return;
    }
    documentContacts.push(documentContact);
  }

  removeDocumentContact(documentContacts: Array<DocumentContact>, index: number) {
    documentContacts.splice(index, 1);
    for (let index in documentContacts) documentContacts[Number(index)].position = Number(index) + 1;
  }

  salutatonAreaContent(documentContact: DocumentContact): string {
    if (this.doc!.is_formal) return `Dear ${this.involvements[documentContact.involvement_id].contact.salutation}`;
    else return `Dear ${this.involvements[documentContact.involvement_id].contact.first_name_salutation}`;
  }

  sanitizeAddress(address: string): string {
    return address.replace(/\n/g, '<br>');
  }

  saveAndCloseDocument() {
    this.saveDocument().then((response_json) => {
      if (response_json && response_json.success) {
        this.newDocument(true);
      }
    });
  }

  saveDocument(autosaved: boolean = false, forOutgoing: boolean = false, forMailMerge: boolean = false): Promise<any> {
    if (!autosaved) {
      if (forOutgoing) this.alertService.alert_status_info_overlay('Saving document first - please wait');
      else this.alertService.alert_status_info_overlay('Saving document - please wait');
      this.doc!.autosaved = false;
      this.clearTimerSubscription();
    } else this.doc!.autosaved = true;
    let docTemplateItem = this.documentStoreService.documentFormComponent!.docTemplateItem;
    for (var i in this.doc!.document_parts) {
      this.doc!.document_parts[Number(i)].position = Number(i);
      for (let editor of tinymce.editors) if (editor.id == `tinymce-${i}-document-part-${i}`) this.doc!.document_parts[Number(i)].contents = editor.getContent();
    }
    for (var i in this.documentToContacts) this.documentToContacts[Number(i)].position = Number(i);
    for (var i in this.documentCcContacts) this.documentCcContacts[Number(i)].position = Number(i);
    for (var i in this.documentBccContacts) this.documentBccContacts[Number(i)].position = Number(i);
    let data: any = {
      id: this.doc!.id,
      document: this.doc,
      document_to_contacts: this.documentToContacts,
      document_cc_contacts: this.documentCcContacts,
      document_bcc_contacts: this.documentBccContacts,
      history_note: this.extraHistoryMessage,
      initial_run: true,
      margin_settings: this.marginSettings,
      template_return_data: this.documentStoreService.documentFormComponent!.templateReturnData,
      font_size: this.fontSize || 12,
      tag_list: this.tagsList,
    };
    if (forMailMerge) data.mail_merge = true;
    let scope = this;
    return this.documentService
      .saveDocument(data)
      .then((response_json) => {
        this.alertService.alert_status_clear_overlay();
        if (response_json.success) {
          if (autosaved) this.alertService.alert_status_success('Document autosaved');
          else this.alertService.alert_status_success(response_json.text || 'Document saved');
          if (response_json.document_id) this.doc!.id = response_json.document_id;
          if (!autosaved && !forOutgoing && this.matterStoreService.billingRecordTimeComponent) this.matterStoreService.billingRecordTimeComponent.addBilling(this.doc!);
          if (!autosaved && !forOutgoing) {
            //this.alertService.alert_status_info_overlay('Verifying document content');
            //this.documentService.wordCount(response_json.document_id || data.id)
            //	.then(response_json => {
            this.alertService.alert_status_clear_overlay();
            //		console.log(this.documentStoreService.documentFormComponent.totalWordCount);
            //		if(response_json.total_word_count == this.documentStoreService.documentFormComponent.totalWordCount){
            //			this.alertService.alert_status_success('Document content verified');
            this.alertService.alert_status_success('Document content saved'); // To be removed if doc verify enabled
            // Keep the form populated so the user can regenerate with changes
            this.setIds(response_json);
            //		}else
            //			this.alertService.alert_status_danger(response_json.text || 'Error verifying document content');
            //	}).catch(() => {
            //		this.alertService.alert_status_clear_overlay();
            //		this.alertService.alert_status_danger('Error verifying document content');
            //	});
            this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
          } else if (!autosaved) {
            this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
            this.setIds(response_json);
          } else {
            this.setIds(response_json);
          }
          this.faviconService.faviconChange('success');
          let scope: DocumentSidebarComponent = this;
          setTimeout(() => {
            if (scope && scope.faviconService) scope.faviconService.faviconChange(autosaved ? 'info' : null);
          }, 2000);
          this.documentStoreService.documentFormComponent!.oldWordCount = this.documentStoreService.documentFormComponent!.totalWordCount;
          return response_json;
        } else {
          this.alertService.alert_status_danger(response_json.text || 'Error saving document - error in server');
          return response_json;
        }
      })
      .catch(function () {
        scope.alertService.alert_status_clear_overlay();
        scope.alertService.alert_status_danger('Error saving document - error in server');
      });
  }

  scrollToPreview() {
    jQuery('html, body').animate({ scrollTop: jQuery(this.documentStoreService.documentFormComponent!.previewIframeElement.nativeElement).offset().top }, 500);
  }

  setAddress(documentContact: DocumentContact) {
    documentContact.address_id = documentContact.address.id;
    documentContact.copy_of_address = documentContact.address.full_address;
  }

  setEmailAddress(documentContact: DocumentContact) {
    documentContact.email_address_id = documentContact.email_address.id;
    documentContact.copy_of_email_address = documentContact.email_address.email;
  }

  setFormal(formal: boolean = true) {
    if (this.doc) {
      this.doc.is_formal = formal;
      this.updateSalutationDisplay();
    }
  }

  setIds(response_json: any) {
    this.doc!.name = response_json.document_name;
    this.doc!.id = response_json.document_id;
    for (let index in response_json.document_part_ids) this.doc!.document_parts[Number(index)].id = response_json.document_part_ids[index];
    for (let index in response_json.document_to_contact_ids) this.documentToContacts[Number(index)].id = response_json.document_to_contact_ids[index];
    for (let index in response_json.document_cc_contact_ids) this.documentCcContacts[Number(index)].id = response_json.document_cc_contact_ids[index];
    for (let index in response_json.document_bcc_contact_ids) this.documentBccContacts[Number(index)].id = response_json.document_bcc_contact_ids[index];
  }

  subscribeToTimer() {
    console.log('Subscribing to autosave timer');
    if (!this.autosaveTimerSubscription) this.autosaveTimerSubscription = this.autosaveTimer.subscribe((timesCalled) => this.autosave(timesCalled));
  }

  toAreaContent(documentContact: DocumentContact): string {
    let content = `${documentContact.copy_of_contact}<br />`;
    if (documentContact.copy_of_address) content += documentContact.copy_of_address.replace(/\n/g, '<br>');
    else if (documentContact.send_by_email && documentContact.copy_of_email_address) content += documentContact.copy_of_email_address;
    else if (documentContact.send_by_fax && documentContact.copy_of_fax) content += documentContact.copy_of_fax;
    else content += documentContact.copy_of_email_address || documentContact.copy_of_fax;
    return content;
  }

  updateCCDisplay() {}

  updateCcPosition() {
    for (let index in this.documentCcContacts) this.documentCcContacts[Number(index)].position = Number(index) + 1;
    this.updateCCDisplay();
  }

  updateSalutationDisplay() {
    let salutationContent = '';
    if (this.documentToContacts.length > 0) salutationContent = this.salutatonAreaContent(this.documentToContacts[0]);
    let documentParts = this.documentStoreService.documentFormComponent!.doc!.document_parts;
    for (let documentPart of documentParts) if (documentPart.type == 'salutation' || documentPart.is_salutation) documentPart.contents = salutationContent;
  }

  updateToDisplay() {
    let toContent = '';
    if (this.documentToContacts.length > 0) toContent = this.toAreaContent(this.documentToContacts[0]);
    let documentParts = this.documentStoreService.documentFormComponent!.doc!.document_parts;
    for (let documentPart of documentParts) if (documentPart.type == 'to' || documentPart.is_to) documentPart.contents = toContent;
  }

  updateToPosition() {
    for (let index in this.documentToContacts) this.documentToContacts[Number(index)].position = Number(index) + 1;
    this.updateToDisplay();
    this.updateSalutationDisplay();
  }
}
